// LandingPagePreview.tsx
import React, { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { MenuCardList } from '../organisms/MenuCardList';
import { BannerWithSearchBar } from '../organisms/BannerWithSearchBar';
import { SectionData } from '../../interfaces';
import { NotificationBanner } from '../molecules/NotificationBanner';
import { SectionGridPreview } from '../organisms/SectionGridPreview';
import { PopularArticlesPreview } from './PopularArticlesPreview';
import { NewsSectionPreview } from '../organisms/NewsSectionPreview';
import { NewsArticle } from '../organisms/NewsSection';
import { Layout } from '../templates';

interface TranslationData {
  configs: {
    needHelpText: string;
  };
  data: {
    gameTitle: string;
    platforms: string[];
    newsArticles: { id: string; title: string }[];
    popularArticles: { id: string; title: string; category: string }[];
    menuCards: { id: string; title: string }[];
    footerLinks: { id: number; text: string }[];
    sectionIcons: { id: string; name: string }[];
    categoryHeadings: { [key: string]: string }[];
    helperTexts: { [key: string]: string }[];
    contactUsFormData: {
      id: string;
      question?: string;
      description?: string;
      placeholder?: string;
      options?: string;
    }[];
    footerNote: string;
    notifications: { id: string; message: string }[];
  };
}
interface LandingPagePreviewProps {
  settings: any;
  translations: TranslationData;
  sectionsData: SectionData[] | null;
  newsArticles: NewsArticle[];
  onClickMyTickets: () => void;
  onClickLandingPageButton: () => void;
  onContactUsClick: () => void;
  onClickMore: (title: string) => void;
  onClickFaq: (id: string) => void;
  onClickPopularArticlePreview: (publish_id: string | number) => void;
  onClickNewsArticlePreview: (id: string) => void;
}

export const LandingPagePreview: React.FC<LandingPagePreviewProps> = ({
  settings,
  sectionsData,
  newsArticles,
  onClickMyTickets,
  onClickLandingPageButton,
  onContactUsClick,
  translations,
  onClickMore,
  onClickFaq,
  onClickPopularArticlePreview,
  onClickNewsArticlePreview,
}) => {
  const onclickMenuCardPreview = () => {};
  const { t }: { t: (key: string, options?: { returnObjects: boolean }) => any } = useTranslation();
  const menuListConfig = settings.data.menuCards;
  const menuListTranslations = translations.data.menuCards;
  const menuList = menuListConfig.map((menuCard: { id: string }) => ({
    ...menuCard,
    ...menuListTranslations.find(trans => trans.id === menuCard.id),
  }));

  // const popularArticleList = t('data.popularArticles', { returnObjects: true }) as FaqArticle[];

  const newsArticlesTranslations = translations.data.newsArticles;
  const newsArticleList: NewsArticle[] = newsArticles.map(article => {
    const translation = newsArticlesTranslations.find(trans => trans.id === article.id);

    return {
      ...article,
      title: translation?.title || article.title,
    };
  });
  const notificationsList = useMemo(() => {
    return settings?.data.notifications.map((notification: { id: string }) => ({
      ...notification,
      ...translations.data.notifications.find((trans: any) => trans.id === notification.id),
    }));
  }, [settings?.data.notifications, translations.data.notifications]);

  const categoryHeadings = t('data.categoryHeadings', { returnObjects: true });
  const helperTexts = t('data.helperTexts', { returnObjects: true });
  return (
    settings && (
      <Layout
        pageBgColor={settings?.configs.secondaryBgColor}
        onClickLandingPageButton={onClickLandingPageButton}
        onClickMyTickets={onClickMyTickets}
        onContactUsClick={onContactUsClick}
        settings={settings}
        isPreviewMode
      >
        <Box
          // border="1px solid #ccc"
          borderRadius="8px"
          overflow="hidden"
          margin="auto"
          backgroundColor={settings.configs.secondaryBgColor}
        >
          {settings?.configs.showBanner && (
            <BannerWithSearchBar
              searchBarPx={{
                base: '2vw',
                md: '1vw',
                lg: '7vw',
              }}
              isFullScreen={settings?.configs.isBannerFullScreen}
              px={{
                base: '0vw',
                md: '0vw',
                lg: settings?.configs.isBannerFullScreen ? '0vw' : '7vw',
              }} // set 7vw to column size and 0vw for full screen banner
              imageSrc={settings.configs.bannerImageUrl}
              altText="Banner image"
              bannerText="HOW CAN WE HELP YOU?"
              placeholderText="Search for articles"
              placeholderFontSize={{ base: '18px', md: '20px', lg: '22px' }}
              showOverlay={settings?.configs.showOverlay}
              showSearchBar={settings?.configs.showSearchBar}
              bannerTextColor={settings?.configs.bannerTextColor}
              bgColor={settings?.configs.secondaryBgColor}
              showJaggedBorder={settings?.configs.showJaggedBorder}
              secondaryAccentColor={settings?.configs.secondaryAccentColor}
              placeholderFontWeight="bold"
              isPreview
            />
          )}
          <Box>
            {settings?.configs.showNotificationBanner && (
              <Box px={{ base: '2vw', md: '1vw', lg: '7vw' }}>
                <NotificationBanner
                  notificationBannerBgColor={settings?.configs.notificationBannerBgColor ?? ''}
                  notificationBannerTextColor={settings?.configs.notificationBannerTextColor ?? ''}
                  notifications={settings?.data.notifications}
                  notificationTranslations={notificationsList}
                />
              </Box>
            )}

            {settings?.configs.showMenuCards && (
              <MenuCardList
                items={settings?.data.menuCards}
                itemsTranslations={menuList}
                menuCardsBgColor={settings.configs.secondaryBgColor}
                menuCradsTextColor={settings.configs.secondaryTextColor}
                onClickMenuCard={onclickMenuCardPreview}
              />
            )}
            {settings?.configs.showNewsSection && (
              <Box
                px={{ base: '2vw', md: '1vw', lg: '6.5vw' }}
                bgGradient={`linear(to-b, white, ${settings?.configs.tertiaryAccentColor})`}
              >
                <NewsSectionPreview
                  onClickNewsArticlePreview={onClickNewsArticlePreview}
                  newsArticles={newsArticles}
                  newsArticlesTranslations={newsArticleList}
                  newsArticleTextColor={settings.configs.primaryTextColor}
                  newsSectionHeadingTextColor={settings.configs.secondaryTextColor}
                  primaryAccentColor={settings?.configs.primaryAccentColor}
                  secondaryAccentColor={settings?.configs.secondaryAccentColor}
                  gradientAccentColor={settings.configs.gradientAccentColor}
                  newsHeadlineTextColor={settings?.configs.newsHeadlineTextColor}
                  newsSectionHeadingText={
                    categoryHeadings.find(
                      (item: Record<string, string>) => 'newsArticlesHeading' in item,
                    )?.newsArticlesHeading
                  }
                  newsSectionHelperTextView={
                    helperTexts.find((item: Record<string, string>) => 'viewAll' in item)?.viewAll
                  }
                  newsSectionHelperTextReadMore={
                    helperTexts.find((item: Record<string, string>) => 'readMore' in item)?.readMore
                  }
                />
              </Box>
            )}
            <Box px={{ base: '2vw', md: '1vw', lg: '6.5vw' }}>
              {settings?.configs.showPopularArticles && (
                <PopularArticlesPreview
                  faqArticles={settings?.data.popularArticles}
                  bgColor={settings.configs.secondaryBgColor}
                  faqArticlesTextColor={settings.configs.primaryTextColor}
                  faqArticlesBgColor={settings.configs.secondaryBgColor}
                  secondaryTextColor={settings?.configs.secondaryTextColor ?? ''}
                  onClickPopularArticle={onClickPopularArticlePreview}
                  popularArticlesHeading={
                    categoryHeadings.find(
                      (item: Record<string, string>) => 'popularArticlesHeading' in item,
                    )?.popularArticlesHeading
                  }
                  faqsHeading={
                    categoryHeadings.find((item: Record<string, string>) => 'faqsHeading' in item)
                      ?.faqsHeading
                  }
                  primaryAccentColor={settings?.configs.primaryAccentColor}
                  secondaryAccentColor={settings?.configs.secondaryAccentColor}
                  tertiaryAccentColor={settings?.configs.tertiaryAccentColor}
                  gradientAccentColor={settings.configs.gradientAccentColor}
                  faqBodyCharLimit={settings.configs.faqBodyCharLimit}
                />
              )}
            </Box>
            <Box>
              <Box px={{ base: '2vw', md: '1vw', lg: '6.5vw' }}>
                {/* section wise faq display */}
                {settings?.configs.showSectionGrid && (
                  <SectionGridPreview
                    textColor={settings?.configs.secondaryTextColor}
                    sectionBgColor={settings?.configs.secondaryBgColor}
                    sections={sectionsData}
                    sectionIcons={settings?.data.sectionIcons}
                    sectionsHeading={
                      categoryHeadings.find(
                        (item: Record<string, string>) => 'categoriesHeading' in item,
                      )?.categoriesHeading
                    }
                    sectionHelperText={
                      helperTexts.find((item: Record<string, string>) => 'more' in item)?.more
                    }
                    sectionCardTextColor={settings?.configs.primaryTextColor}
                    primaryAccentColor={settings?.configs.primaryAccentColor}
                    secondaryAccentColor={settings?.configs.secondaryAccentColor}
                    categoriesHeadingTextColor={settings?.configs.categoriesHeadingTextColor}
                    onClickMore={onClickMore}
                    onClickFaq={onClickFaq}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Layout>
    )
  );
};
